/**
 * Disponibilité du matériel sur une période (proxy Quilium `/fr/api-rentable-materiel`).
 *
 * Le GMS n'expose aucun stock daté : `/public/materials` rend l'inventaire catalogue
 * et ignore les paramètres de date. Le restant se reconstitue depuis les réservations
 * qui chevauchent la période, chacune portant ses matériels avec `quantity` (engagé)
 * et `inventory` (stock total) :
 *
 *     restant = inventory − Σ quantity des réservations qui chevauchent
 *
 * ⚠️ Piège n°1 — lignes dupliquées. L'API rend des *RentDates*, donc une même
 * réservation sort sur PLUSIEURS lignes, et toutes ne portent pas les matériels.
 * Relevé réel sur salle-aula-redange (25/02 → 05/03/2026), un rent sort 3 fois —
 * une ligne à 0 matériel, deux à 4 :
 *
 *     somme brute des lignes .................. Chaises = 180  (le double)
 *     dédup par id, 1re ligne gardée .......... Chaises = 0    (stock annoncé libre !)
 *     dédup par id, MAX par matériel .......... Chaises = 90   ✅
 *
 * ⚠️ Piège n°2 — la fenêtre demandée découpe les lignes. Interroger EXACTEMENT la
 * période voulue ne renvoie pas toutes les lignes d'une réservation, et celles qui
 * manquent sont justement celles qui portent les matériels. Mesuré sur le rent du
 * 01/03/2026, en faisant varier la seule fenêtre :
 *
 *     start=end=2026-03-01 .......... 1 ligne,  0 avec matériel -> Chaises = 0   ✗
 *     2026-02-28 → 2026-03-02 ....... 2 lignes, 1 avec matériel -> Chaises = 90
 *     2026-02-22 → 2026-03-08 ....... 3 lignes, 2 avec matériel -> Chaises = 90  ✅
 *
 * On élargit donc la fenêtre de PADDING_JOURS de chaque côté, puis on refait le test
 * de chevauchement ICI. Sans cela, réserver sur une seule journée annonçait libre du
 * matériel intégralement pris — le pire des échecs possibles pour ce contrôle.
 *
 * ⚠️ Piège n°3 — plafond de 100 par page, troncature silencieuse. Mesuré sur le
 * rentable Open Air, année 2026 : `meta.total` = 143, `data` = 100, `last_page` = 2.
 * Rien dans le corps de la réponse ne signale qu'il manque 43 lignes. Prendre une
 * réponse pleine pour un inventaire complet sous-estime l'engagement, donc SURESTIME
 * la disponibilité — la direction dangereuse.
 *
 * On ne découpe pas la fenêtre pour y remédier : rétrécir la plage rouvre le piège
 * n°2. L'API est paginée (`page=N`), on suit donc les pages, ce qui rend des données
 * complètes sans toucher à la fenêtre. `perPage` et `limit` sont ignorés, le pas
 * reste 100.
 *
 * Voir docs/gemeng-app/public-api.md, « Recette : disponibilité du matériel ».
 */

const ROUTE = '/fr/api-rentable-materiel';

// Marge appliquée de part et d'autre de la période demandée (cf. piège n°2).
// À ±7 jours les lignes porteuses de matériel étaient déjà toutes là ; ±14 garde
// de l'air sans approcher le plafond au-delà duquel l'API rend un tableau vide.
const PADDING_JOURS = 14;

const JOUR_MS = 24 * 60 * 60 * 1000;

// Le GMS ne décompte que ces deux états ; une demande refusée ou annulée doit rendre
// son matériel au stock.
const ETATS_ENGAGEANTS = ['active', 'pending'];

/** clé d'appel -> Promise<Map|null>, pour ne pas relancer le même appel en vol. */
const cache = new Map();

function isoDay(value) {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return (
        d.getFullYear() +
        '-' +
        String(d.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(d.getDate()).padStart(2, '0')
    );
}

/**
 * La réservation touche-t-elle [debutMs, finMs] ?
 *
 * Refait ici parce que la fenêtre envoyée à l'API est volontairement plus large que
 * la période voulue (cf. piège n°2) : sans ce filtre, on compterait comme engagé du
 * matériel réservé deux semaines avant ou après.
 */
function chevauche(rent, debutMs, finMs) {
    const debut = Date.parse(rent.rentStart);
    const fin = Date.parse(rent.rentEnd);
    if (isNaN(debut) || isNaN(fin)) return false;
    return debut <= finMs && fin >= debutMs;
}

/**
 * Réservations brutes -> Map(materialId -> {inventory, engaged, remaining}).
 *
 * @param {Array} rows lignes rendues par le proxy, sur la fenêtre ÉLARGIE
 * @param {number} debutMs début de la période réellement demandée
 * @param {number} finMs fin de la période réellement demandée (fin de journée)
 */
function agreger(rows, debutMs, finMs) {
    if (!Array.isArray(rows)) return null;

    // Temps 1 : par réservation, la quantité MAXIMALE vue pour chaque matériel.
    // C'est ce qui neutralise les lignes dupliquées, y compris celles à 0.
    const parRent = new Map();
    const inventaire = new Map();

    rows.forEach((rent) => {
        if (!ETATS_ENGAGEANTS.includes(rent.state)) return;
        if (!chevauche(rent, debutMs, finMs)) return;
        if (!parRent.has(rent.id)) parRent.set(rent.id, new Map());
        const materiels = parRent.get(rent.id);

        (rent.materials || []).forEach((m) => {
            inventaire.set(m.id, m.inventory || 0);
            const quantite = m.quantity || 0;
            if (quantite > (materiels.get(m.id) || 0)) materiels.set(m.id, quantite);
        });
    });

    // Temps 2 : somme entre réservations.
    const engage = new Map();
    parRent.forEach((materiels) => {
        materiels.forEach((quantite, id) => {
            engage.set(id, (engage.get(id) || 0) + quantite);
        });
    });

    const dispo = new Map();
    inventaire.forEach((stock, id) => {
        const pris = engage.get(id) || 0;
        dispo.set(id, {
            inventory: stock,
            engaged: pris,
            remaining: Math.max(0, stock - pris),
        });
    });

    return dispo;
}

/**
 * Disponibilité du matériel d'un rentable sur [startDate, endDate].
 *
 * @returns {Promise<Map|null>} Map(materialId -> {inventory, engaged, remaining}),
 *   ou `null` si l'appel échoue. Le null est significatif : il dit « je ne sais pas »,
 *   à ne pas confondre avec une Map vide, qui dit « rien n'est engagé ». Les appelants
 *   doivent laisser passer sur un null — le GMS revalide de toute façon à la
 *   soumission, et couper le formulaire sur un hoquet réseau serait pire que le mal.
 */
export function fetchMaterialAvailability(rentableId, startDate, endDate) {
    const start = isoDay(startDate);
    const end = isoDay(endDate);
    if (!rentableId || !start || !end) return Promise.resolve(null);

    // Bornes de la période réellement demandée, en jours pleins : une réservation
    // qui commence à 18:00 le dernier jour chevauche toujours la demande.
    const debutMs = new Date(`${start}T00:00:00`).getTime();
    const finMs = new Date(`${end}T23:59:59`).getTime();

    // Fenêtre élargie envoyée à l'API (cf. piège n°2 en tête de fichier).
    const fenetreDebut = isoDay(new Date(debutMs - PADDING_JOURS * JOUR_MS));
    const fenetreFin = isoDay(new Date(finMs + PADDING_JOURS * JOUR_MS));

    const base = `${ROUTE}?rentable=${encodeURIComponent(rentableId)}&start=${fenetreDebut}&end=${fenetreFin}`;
    // La clé de cache porte la période DEMANDÉE : deux demandes différentes peuvent
    // partager la même fenêtre élargie mais pas le même résultat.
    const cle = `${base}#${start}_${end}`;
    if (cache.has(cle)) return cache.get(cle);

    const request = chargerToutesLesPages(base)
        .then((rows) => agreger(rows, debutMs, finMs))
        .catch(() => null);

    cache.set(cle, request);
    return request;
}

/**
 * Lit une page du proxy et normalise sa forme.
 *
 * Accepte aussi l'ancienne forme (tableau nu), au cas où le template CMS serait en
 * retard d'un déploiement sur le JS : mieux vaut une page unique exploitable qu'une
 * page d'accueil de formulaire cassée.
 */
function lirePage(url) {
    return fetch(url)
        .then((res) => (res.ok ? res.json() : null))
        .then((corps) => {
            if (Array.isArray(corps)) return { rents: corps, lastPage: 1 };
            if (!corps || !Array.isArray(corps.rents)) return null;
            // `ok: false` = l'appel GMS n'a pas abouti côté proxy (dépassement du
            // timeout, notamment). Le flux est alors vide, ce qui se lirait comme
            // « aucune réservation », donc « tout est libre » : exactement le
            // contresens dangereux. On le remonte comme une ignorance.
            if (corps.ok === false) return null;
            return { rents: corps.rents, lastPage: Number(corps.lastPage) || 1 };
        });
}

/**
 * Toutes les pages du proxy, concaténées.
 *
 * La première page donne `lastPage` ; les suivantes partent en parallèle. Une seule
 * page en échec fait échouer l'ensemble volontairement : un jeu de lignes incomplet
 * produirait un restant surestimé, or on préfère « je ne sais pas » (null) à un
 * chiffre faux.
 */
function chargerToutesLesPages(base) {
    return lirePage(`${base}&page=1`).then((premiere) => {
        if (!premiere) return null;
        if (premiere.lastPage <= 1) return premiere.rents;

        const suivantes = [];
        for (let p = 2; p <= premiere.lastPage; p += 1) {
            suivantes.push(lirePage(`${base}&page=${p}`));
        }

        return Promise.all(suivantes).then((pages) => {
            if (pages.some((page) => !page)) return null;
            return pages.reduce((acc, page) => acc.concat(page.rents), premiere.rents);
        });
    });
}

/**
 * Vide le cache. À appeler avant le contrôle final : entre l'étape « Matériel » et
 * l'envoi, quelqu'un d'autre a pu réserver le même matériel — c'est précisément ce
 * qu'on cherche à détecter.
 */
export function invalidateMaterialAvailability() {
    cache.clear();
}
